import { cn } from "cn";
import {
  appointmentBlockColorClasses,
  ROW_HEIGHT,
} from "@/constants/appointments/appointments-constants";
import type { CalendarBlock } from "@/types/appointments/calendar-types";
import { useDraggable } from "@dnd-kit/core";
import { GripVertical } from "lucide-react";
import { useRef } from "react";
import { calculateResizedDurationRows } from "@/lib/appointments/calendar-utils";
import { useIntl } from "react-intl";
import { isoTime } from "@/lib/utils";

interface AppointmentBlockProps {
  block: CalendarBlock;
  startRow: number;
  durationRows: number;
  onEditClick: (block: CalendarBlock) => void;
  readOnly?: boolean;
}

export function AppointmentBlock({
  block,
  startRow,
  durationRows,
  onEditClick,
  readOnly = false,
}: AppointmentBlockProps) {
  const intl = useIntl();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: String(block.id),
    disabled: readOnly,
    data: {
      lineId: block.id,
      appointmentId: block.appointment_id,
      staffId: String(block.staff_id),
      startRow,
      type: "move",
    },
  });

  const resizeHandleId = `${block.id}-resize`;
  const {
    attributes: resizeAttributes,
    listeners: resizeListeners,
    setNodeRef: setResizeNodeRef,
    transform: resizeTransform,
    isDragging: isResizing,
  } = useDraggable({
    id: resizeHandleId,
    disabled: readOnly,
    data: {
      lineId: block.id,
      appointmentId: block.appointment_id,
      staffId: String(block.staff_id),
      startRow,
      durationRows,
      durationMinutes: block.duration_minutes,
      type: "resize",
    },
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  const newDurationRows = resizeTransform
    ? calculateResizedDurationRows(resizeTransform.y, durationRows, startRow)
    : durationRows;
  const resizeHeight = `${newDurationRows * ROW_HEIGHT}px`;

  const dragOccurredRef = useRef(false);
  if (isDragging) {
    dragOccurredRef.current = true;
  }

  return (
    <div
      ref={setNodeRef}
      style={{
        top: `${startRow * ROW_HEIGHT}px`,
        height: isResizing ? resizeHeight : `${durationRows * ROW_HEIGHT}px`,
        ...style,
      }}
      {...(readOnly ? {} : listeners)}
      {...(readOnly ? {} : attributes)}
      onClick={
        readOnly
          ? undefined
          : (event) => {
              event.stopPropagation();
              if (dragOccurredRef.current) {
                dragOccurredRef.current = false;
                return;
              }
              onEditClick(block);
            }
      }
      className={cn(
        "absolute left-0 right-0 rounded-sm px-2 py-1 text-xs z-10 flex flex-col",
        appointmentBlockColorClasses(block.category_id),
        block.status === "completed" && "opacity-60",
        readOnly
          ? "cursor-default"
          : "cursor-grab active:cursor-grabbing",
        // Note: z-index is kept below header (z-50) so blocks don't overlap the sticky header
        isDragging && "opacity-50 z-20"
      )}
    >
      <div className="flex justify-between items-start">
        <span className="font-semibold truncate pr-2">{block.client_name}</span>
        <span className="font-medium shrink-0">{intl.formatTime(isoTime(block.start_time), { format: "clock" })}</span>
      </div>

      <span className="truncate mt-0.5 opacity-80 flex-1">
        {block.service_name}
      </span>

      {!readOnly && (
        <div
          ref={setResizeNodeRef}
          {...resizeListeners}
          {...resizeAttributes}
          className={cn(
            "absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize flex items-center justify-center group",
            "hover:bg-foreground/10 transition-colors"
          )}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <GripVertical className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      )}
    </div>
  );
}
